import { zodResolver } from '@hookform/resolvers/zod';
import { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Autocomplete,
  FileMimeTypes,
  FilePicker,
  ItemStatus,
  S3FileUploader,
} from '@/modules/common';

import { Model, ModelsAttributes, ModelsUploadUrlData } from '../types';
import { ModelFormSchema } from '../validations';

type ModelFormValues = z.infer<typeof ModelFormSchema>;

interface ModelFormProps {
  editMode?: boolean;
  modelDetails?: Model;
  options: ModelsAttributes;
  uploadUrlData?: ModelsUploadUrlData | null;
  defaultValues?: ModelFormValues;
  refetchUploadCredentials: () => Promise<void>;
  onSubmit: (data: ModelFormValues) => Promise<void>;
  children: (props: { isLoading: boolean }) => JSX.Element;
}

const defaultFormValues = {
  name: '',
  colorId: '',
  typeId: '',
  genderId: '',
  status: '',
  publicUrl: '',
  updatedAt: undefined,
};

export function ModelForm({
  onSubmit,
  children,
  options,
  editMode,
  modelDetails,
  defaultValues,
  uploadUrlData,
  refetchUploadCredentials,
}: ModelFormProps) {
  const form = useForm<ModelFormValues>({
    resolver: zodResolver(ModelFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const handleFormSubmit = async () => {
    await onSubmit(form.getValues());
  };

  return (
    <Form {...form}>
      <S3FileUploader
        config={[
          {
            key: 'publicUrl',
            uploadUrl: uploadUrlData?.url,
            fileUrl: defaultValues?.publicUrl,
            fileObject: modelDetails?.fileObject,
          },
        ]}
        onComplete={handleFormSubmit}
        refetchCredentials={refetchUploadCredentials}
      >
        {({
          retryUpload,
          filesState,
          isUploading,
          onFileChange,
          uploadFiles,
        }) => (
          <form onSubmit={form.handleSubmit(uploadFiles)}>
            {children({ isLoading: isUploading })}

            <div className="flex flex-col gap-10 max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-1 grid-flow-col grid-rows-7 sm:grid-rows-3 xl:grid-rows-2 lg:grid-rows-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Model name</FormLabel>
                      <FormControl>
                        <Input placeholder="Model name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Autocomplete
                  name="colorId"
                  label="Model color"
                  control={form.control}
                  options={options.colors}
                  placeholder="Select color"
                />

                <Autocomplete
                  name="typeId"
                  label="Model type"
                  control={form.control}
                  options={options.types}
                  placeholder="Select type"
                />

                <Autocomplete
                  name="fileNameId"
                  label="File name"
                  control={form.control}
                  options={options.fileNames}
                  placeholder="Select file name"
                />

                <Autocomplete
                  name="genderId"
                  label="Gender"
                  control={form.control}
                  options={options.genders}
                  placeholder="Select gender"
                />

                <FormField
                  control={form.control}
                  name="updatedAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last modified</FormLabel>
                      <FormControl>
                        <Input
                          disabled
                          placeholder="YYYY-MM-DD"
                          required={false}
                          readOnly
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <ItemStatus
                  name="status"
                  isDisabled={!editMode}
                  control={form.control}
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2">
                <FilePicker
                  control={form.control}
                  label="3D Model"
                  name="publicUrl"
                  onRetry={retryUpload}
                  accept={[FileMimeTypes.Scn]}
                  placeholder="Upload 3D Model"
                  tips={
                    <p className="text-sm text-zinc-500">
                      The file must be in{' '}
                      <span className="text-zinc-100">.scn</span> format
                    </p>
                  }
                  isUploading={isUploading}
                  file={filesState.publicUrl?.file}
                  error={filesState.publicUrl?.error}
                  onFileChange={onFileChange}
                />
              </div>
            </div>
          </form>
        )}
      </S3FileUploader>
    </Form>
  );
}

export default ModelForm;
